import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";

import Table from "@/components/Table";
import api from "@/services/api";
import TopMenu from "./components/TopMenu";
import ItemModal from "../components/itemModal";

export default ({ project }) => {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    search: "",
    statuses: ["TODO", "READY_TO_DEV", "IN_PROGRESS", "NEED_INFO"],
    urgent: [],
    module_ids: [],
    roadlog_tags: [],
    is_in_backlog: "",
  });
  const [budgets, setBudgets] = useState([]);
  const [sortBy, setSortBy] = useState({ key: "points", value: 1 });
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const itemModalId = query.get("item_modal_id");

  useEffect(() => {
    fetchItems();
  }, [filters]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/roadlog_item/search", { project_id: project._id, exclude_categories: ["BUG"], ...filters });
      if (!ok) return toast.error("Failed to fetch roadlog items");

      const { data: budgetsData } = await api.post("/budget/search", { projectId: project._id, status: "active" });
      setBudgets(budgetsData);
      setItems(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch roadlog items");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (itemId, value) => {
    try {
      const { ok, data } = await api.put(`/roadlog_item/${itemId}`, value);
      if (!ok) return toast.error("Failed to update");

      const index = items.findIndex((item) => item._id === itemId);
      const newItems = [...items];
      newItems[index] = { ...newItems[index], ...value };
      setItems(newItems);
      toast.success(`Updated successfully`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update");
    }
  };

  const totalDays = items.reduce((acc, item) => acc + Number(item.days_required || 0), 0);
  const totalEstimation = items.reduce((acc, item) => acc + Number(item.estimation || 0), 0);

  return (
    <div className="space-y-1">
      <TopMenu
        filters={filters}
        setFilters={setFilters}
        project={project}
        items={items}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        onMultipleDelete={() => {
          setItems(items.filter((i) => !selectedItems.some((s) => s._id === i._id)));
          setSelectedItems([]);
        }}
        onTicketCreated={(newTicket) => {
          setItems([...items, newTicket]);
        }}
      />

      <Table
        total={items.length}
        header={[
          {
            title: (
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedItems(items);
                  } else {
                    setSelectedItems([]);
                  }
                }}
              />
            ),
            position: "center",
          },
          { title: "Title", key: "title" },
          { title: "Budget", key: "budget_name" },
          { title: "Days Required", key: "days_required" },
          { title: "Estimation", key: "estimation" },
          { title: "Status", key: "status" },
        ]}
        loading={loading}
        height="h-[32rem]"
        sort={{ [sortBy.key]: sortBy.value }}
        onSort={(e) => setSortBy(sortBy.key === e ? { key: e, value: sortBy.value * -1 } : { key: e, value: 1 })}>
        <AnimatePresence>
          {items
            .sort((a, b) => {
              if (sortBy.key === "created_at") return sortBy.value * (new Date(b.created_at) - new Date(a.created_at));
              if (sortBy.key === "points") return sortBy.value * (b.points - a.points);
              if (["days_required", "estimation", "index"].includes(sortBy.key)) {
                return sortBy.value * ((a[sortBy.key] || 0) - (b[sortBy.key] || 0));
              }
              return sortBy.value * (a[sortBy.key] || "").localeCompare(b[sortBy.key] || "");
            })
            .map((item, index) => {
              const selected = selectedItems.some((i) => i._id === item._id);
              return (
                <motion.tr
                  key={item._id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className={`${index % 2 === 0 ? "bg-gray-100" : "bg-gray-50"} cursor-pointer group`}>
                  <td
                    className="border px-4 py-2 cursor-pointer text-center"
                    onClick={(e) => {
                      if (selected) {
                        setSelectedItems(selectedItems.filter((i) => i._id !== item._id));
                      } else {
                        setSelectedItems([...selectedItems, item]);
                      }
                    }}>
                    <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={selected} onChange={() => {}} />
                  </td>

                  <td
                    onClick={() => {
                      query.set("item_modal_id", item._id);
                      navigate({ search: query.toString() });
                    }}
                    className="border px-4 py-2 text-sm hover:bg-gray-200">
                    <div className="flex flex-col">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">#{item.index}</span>
                          <span className="line-clamp-3">{item.title}</span>
                        </div>
                        {item.user_avatar && (
                          <div className="group relative">
                            <img src={item.user_avatar} alt={item.user_name} className="h-6 w-6 rounded-full" />
                            <div className="absolute -top-8 right-0 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                              {item.user_name}
                            </div>
                          </div>
                        )}
                      </div>
                      {item.module_name && (
                        <div className="mt-1">
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">{item.module_name}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <EditableSelectCell
                    value={{ name: item.budget_name, _id: item.budget_id }}
                    options={budgets.map((budget) => ({ name: budget.name, _id: budget._id }))}
                    onSave={(e) => {
                      if (!e) return handleSave(item._id, { budget_id: "", budget_name: "" });
                      handleSave(item._id, { budget_id: e._id, budget_name: e.name });
                    }}>
                    {item.budget_name}
                  </EditableSelectCell>
                  <td className="border px-4 py-2 max-w-xs truncate">{item.days_required}</td>
                  <td className="border px-4 py-2 max-w-xs truncate">{formatNumberThreeDigits(item.estimation || 0)} €</td>
                  <EditableSelectCell
                    value={{ name: item.status, _id: item.status }}
                    options={["TODO", "IN_PROGRESS", "NEED_INFO", "DONE"].map((status) => ({ name: status, _id: status }))}
                    onSave={(e) => {
                      handleSave(item._id, { status: e._id });
                    }}>
                    <StatusTag item={item} />
                  </EditableSelectCell>
                </motion.tr>
              );
            })}
        </AnimatePresence>
        <tr className="bg-sky-700 text-white font-bold">
          <td className="border px-4 py-2 max-w-xs truncate text-sm">Total</td>
          <td className="border px-4 py-2 max-w-xs truncate text-sm"></td>
          <td className="border px-4 py-2 max-w-xs truncate text-sm"></td>
          <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalDays.toFixed(2)}</td>
          <td className="border px-4 py-2 max-w-xs truncate text-sm">{formatNumberThreeDigits(totalEstimation)} €</td>
          <td className="border px-4 py-2 max-w-xs truncate text-sm"></td>
        </tr>
      </Table>

      {itemModalId && (
        <ItemModal
          project={project}
          onClose={() => {
            query.delete("item_modal_id");
            navigate({ search: query.toString() });
          }}
          onUpdate={(item) => setItems(items.map((i) => (i._id === item._id ? item : i)))}
          onDelete={(item) => setItems(items.filter((i) => i._id !== item._id))}
        />
      )}
    </div>
  );
};

const EditableSelectCell = ({ value, options, onSave, children }) => {
  const [editing, setEditing] = useState(false);
  const cellRef = useRef(null);

  const handleSave = async (e) => {
    setEditing(false);
    const find = options.find((f) => f._id === e.target.value);
    await onSave(find);
  };

  const handleClickOutside = (event) => {
    if (cellRef.current && !cellRef.current.contains(event.target)) {
      setEditing(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <td ref={cellRef} onClick={() => setEditing(true)} onBlur={() => setEditing(false)} className="border px-4 py-2 max-w-xs truncate text-sm hover:bg-gray-200">
      {editing ? (
        <select className="input p-1" value={value?._id} onChange={handleSave}>
          <option value=""></option>
          {options.map((option) => (
            <option key={option._id} value={option._id}>
              {option.name}
            </option>
          ))}
        </select>
      ) : (
        children
      )}
    </td>
  );
};

const StatusTag = ({ item }) => {
  if (!item?.status) return null;
  const colors = {
    TODO: "bg-blue-600 text-xs text-white border-gray-700",
    IN_PROGRESS: "bg-blue-100 text-xs text-blue-800",
    NEED_INFO: "bg-yellow-100 text-xs text-yellow-800",
    DONE: "bg-green-100 text-xs text-green-800",
  };
  return <div className={`px-2 py-1 rounded w-fit ${colors[item?.status]}`}>{item?.status}</div>;
};

const formatNumberThreeDigits = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};
